// components/auth/CreateAccountModal.jsx
'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { FaTimes, FaSpinner } from 'react-icons/fa';

import { logger } from '@/utils/log';
export default function CreateAccountModal({ isOpen, onClose }) {
    const { verifyNewAccountEmail, createNewAccount } = useAuth();
    const [step, setStep] = useState(1); // 1: captcha & email, 2: account details
    const [captcha, setCaptcha] = useState('');
    const [userCaptcha, setUserCaptcha] = useState('');
    const [email, setEmail] = useState('');
    const [accountData, setAccountData] = useState({
        firstName: '',
        lastName: '',
        passCode: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    // Legacy CaptchaError: a red "Give that another try!" under the captcha,
    // separate from the request error box (Login.razor L180-184).
    const [captchaError, setCaptchaError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasMinLength: false,
        passwordsMatch: false
    });

    const generateCaptcha = useCallback(() => {
        const chars = '23456789ABCDEFHJKMNPQRTUVWXYZabcdefghijklmnopqrstuvwxyz';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars[Math.floor(Math.random() * chars.length)];
        }
        setCaptcha(code);
    }, []);

    // Generate the captcha on mount.
    //
    // This was `useState(() => { generateCaptcha(); }, [])`, which is not a
    // mount hook at all: useState treats the function as a LAZY INITIALISER and
    // runs it immediately, during render. `generateCaptcha` is declared with
    // `const` below, so calling it mid-render hit the temporal dead zone and
    // threw `ReferenceError: Cannot access 'generateCaptcha' before
    // initialization` — which Next shows in production as the bare
    // "Application error: a client-side exception has occurred".
    //
    // The modal therefore crashed the LOGIN PAGE the instant it opened, so no
    // new user could create an account at all. Found live on 2026-08-09.
    // Declaration order now also puts generateCaptcha first, so this cannot
    // recur if someone converts the effect back.
    useEffect(() => {
        generateCaptcha();
    }, [generateCaptcha]);

    const validatePassword = (password, confirmPassword) => {
        setPasswordValidation({
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasMinLength: password.length >= 8,
            passwordsMatch: password === confirmPassword && password.length > 0
        });
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();

        // Legacy SubmitNewAccountRequest (Login.razor.cs L716-724): flag the
        // captcha, clear the entry, draw a new one.
        if (userCaptcha !== captcha) {
            setCaptchaError(true);
            setUserCaptcha('');
            generateCaptcha();
            return;
        }

        setCaptchaError(false);
        setError('');
        setLoading(true);

        try {
            const result = await verifyNewAccountEmail(email);

            if (result.success) {
                setStep(2);
                setError('');
                // Legacy L752: toastService.ShowSuccess after the email step.
                toast.success('Verification code has been sent');
            } else {
                setError(result.error || 'Email verification failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            logger.error('Email verification error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAccount = async (e) => {
        e.preventDefault();

        if (accountData.password !== accountData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const result = await createNewAccount({
                email,
                firstName: accountData.firstName,
                lastName: accountData.lastName,
                passCode: accountData.passCode,
                password: accountData.password
            });

            if (result.success) {
                toast.success('Welcome! Your account has been successfully created. You can now log in.');
                handleClose();
            } else {
                setError(result.error || 'Account creation failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            logger.error('Create account error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setLoading(true);
        try {
            const result = await verifyNewAccountEmail(email);
            if (result.success) {
                // Same request, same legacy toast as the first send.
                toast.success('Verification code has been sent');
            } else {
                setError(result.error || 'Failed to resend code');
            }
        } catch (err) {
            logger.error('Resend verification code error:', err);
            setError('Failed to resend code');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setEmail('');
        setUserCaptcha('');
        setCaptchaError(false);
        setAccountData({
            firstName: '',
            lastName: '',
            passCode: '',
            password: '',
            confirmPassword: ''
        });
        setError('');
        generateCaptcha();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto">
            <div className="modal-content animate-scale-in w-full max-w-2xl" onClick={e => e.stopPropagation()}>

                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200">
                    <h4 className="text-gray-900">Create New Account</h4>
                    <button
                        onClick={handleClose}
                        className="btn btn-ghost btn-sm p-1.5 rounded-lg"
                        disabled={loading}
                        aria-label="Close"
                    >
                        <FaTimes className="w-4 h-4" />
                    </button>
                </div>

                {step === 1 ? (
                    <form onSubmit={handleEmailSubmit}>
                        <div className="p-4 sm:p-5 space-y-4">
                            <div className="text-center">
                                <p className="font-semibold mb-4">
                                    First, a quick security checkpoint so we know you're human..
                                    Enter the characters exactly as you see them
                                </p>

                                {/* Captcha display */}
                                <div className="bg-gray-200 rounded-lg p-4 mb-4 inline-block">
                                    <span className="text-2xl font-mono font-bold tracking-wider select-none">
                                        {captcha}
                                    </span>
                                </div>

                                <input
                                    type="text"
                                    value={userCaptcha}
                                    onChange={(e) => setUserCaptcha(e.target.value)}
                                    placeholder="Enter characters"
                                    className="input-field max-w-xs mx-auto"
                                    required
                                    disabled={loading}
                                />
                                {/* Legacy Login.razor L182 — verbatim (audit 12t item 11). */}
                                {captchaError && (
                                    <p className="mt-3 font-bold text-red-600">Give that another try!</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 text-center">
                                    Enter Email:
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter e-mail here"
                                    className="input-field"
                                    required
                                    disabled={loading}
                                />
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 p-4 sm:p-5 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1 btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 btn btn-primary"
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleCreateAccount}>
                        <div className="p-4 sm:p-5 space-y-4">
                            <div className="text-center mb-4">
                                <p className="font-semibold">
                                    A verification code has been sent to: {email}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Enter your one-time code:
                                </label>
                                <input
                                    type="text"
                                    value={accountData.passCode}
                                    onChange={(e) => setAccountData({ ...accountData, passCode: e.target.value })}
                                    placeholder="Enter code here"
                                    className="input-field"
                                    maxLength={14}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="text-center text-sm text-gray-600">
                                <p>Please remember to check your spam folders if you did not receive a code.</p>
                                <p>Still no code? We can resend.</p>
                                <button
                                    type="button"
                                    onClick={handleResendCode}
                                    disabled={loading}
                                    className="mt-2 btn btn-primary btn-sm"
                                >
                                    Resend
                                </button>
                            </div>

                            <hr className="border-gray-200" />

                            <p className="text-center font-semibold">
                                The following e-mail address is now your User Id: {email}
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        First Name:
                                    </label>
                                    <input
                                        type="text"
                                        value={accountData.firstName}
                                        onChange={(e) => setAccountData({ ...accountData, firstName: e.target.value })}
                                        placeholder="Enter first name"
                                        className="input-field"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Last Name:
                                    </label>
                                    <input
                                        type="text"
                                        value={accountData.lastName}
                                        onChange={(e) => setAccountData({ ...accountData, lastName: e.target.value })}
                                        placeholder="Enter last name"
                                        className="input-field"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Enter a Password:
                                    </label>
                                    <input
                                        type="password"
                                        value={accountData.password}
                                        onChange={(e) => {
                                            setAccountData({ ...accountData, password: e.target.value });
                                            validatePassword(e.target.value, accountData.confirmPassword);
                                        }}
                                        placeholder="Enter password"
                                        className="input-field"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirm Password:
                                    </label>
                                    <input
                                        type="password"
                                        value={accountData.confirmPassword}
                                        onChange={(e) => {
                                            setAccountData({ ...accountData, confirmPassword: e.target.value });
                                            validatePassword(accountData.password, e.target.value);
                                        }}
                                        placeholder="Confirm password"
                                        className="input-field"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* Password validation */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                <div className="space-y-1">
                                    <p className={passwordValidation.hasUppercase ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasUppercase ? '✓' : '•'} At least one uppercase letter
                                    </p>
                                    <p className={passwordValidation.hasLowercase ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasLowercase ? '✓' : '•'} At least one lowercase letter
                                    </p>
                                    <p className={passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasNumber ? '✓' : '•'} At least one number
                                    </p>
                                    <p className={passwordValidation.hasMinLength ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasMinLength ? '✓' : '•'} At least 8 characters
                                    </p>
                                </div>
                                <div>
                                    <p className={passwordValidation.passwordsMatch ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.passwordsMatch ? '✓' : '•'} Passwords match
                                    </p>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 p-4 sm:p-5 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1 btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !Object.values(passwordValidation).every(v => v)}
                                className="flex-1 btn btn-primary"
                            >
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}